import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import Cookies from 'js-cookie';
import { createClassicToken, storeCombinedAccessCookie, storeClassicCookie } from './tokenHelper';
import {
  CompanyPolicies,
  DocumentRequestPolicies,
  DocumentPolicies,
  QueryPolicies,
  UserPolicies,
} from './TypePolicies';
import REDIRECT_TO_LOGIN_ON_ERROR_LINK from './redirectToLoginOnErrorLink';
import { doesAppVersionMatch, triggerVersionMismatch } from '@ApplicationWide/version-checking';

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
  },
  query: {
    fetchPolicy: 'no-cache',
  },
};

export default function createApolloClient(baseUrl) {
  const cache = new InMemoryCache({
    typePolicies: {
      Company: CompanyPolicies,
      Query: {
        fields: QueryPolicies,
      },
      CompanyBaseDetails: { merge: false },
      DocumentRequest: DocumentRequestPolicies,
      Document: DocumentPolicies,
      // allow the same doc to be assigned over different packets:
      // without this, the cache collapses all docs with the same id
      // during smart assignment (#105197)
      SmartDocumentTemplate: {
        keyFields: ['id', 'packetId'],
      },
      User: {
        keyFields: ['id'],
        fields: UserPolicies,
      },
    },
  });

  const httpLink = createHttpLink({
    uri: baseUrl ?? '/graphql',
    credentials: 'include',
  });
  const authLink = setContext((_, { headers }) => {
    const combinedAccess = Cookies.get('combinedAccess');
    return {
      headers: {
        ...headers,
        authorization: combinedAccess ? `combinedAccess=${combinedAccess}` : null,
      },
    };
  });

  const afterwareLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext();
      const {
        response: { headers },
      } = context;
      if (headers) {
        const combinedAccessToken = headers.get('CombinedAccessToken');
        if (combinedAccessToken) {
          storeCombinedAccessCookie(combinedAccessToken);
          const classicToken = createClassicToken(combinedAccessToken);
          storeClassicCookie(classicToken);
        }
        const headerVersion = headers.get('X-version');
        if (!doesAppVersionMatch(headerVersion)) {
          triggerVersionMismatch(headerVersion);
        }
      }
      return response;
    });
  });

  return new ApolloClient({
    cache,
    defaultOptions,
    link: from([authLink, REDIRECT_TO_LOGIN_ON_ERROR_LINK, afterwareLink, httpLink]),
  });
}
